/**
 * "Assign to" for an enquiry — pick a person off the Settings list, or unassign.
 *
 * Owner's decisions, 2026-10-02 (closed): the list is free text kept in
 * Settings → Notifications, the LATEST assignment wins, assigning emails the
 * assignee (CC the assigner), and unassigning sends NO email. A person removed
 * from the list keeps their snapshot on existing assignments and shows as
 * "(removed)", but is not offered here.
 *
 * ⚠️ THE ASSIGNMENT AND THE EMAIL ARE TWO FACTS. A `200` whose `email.status`
 * is `skipped` SAVED the assignment and did not queue the email — so it is
 * reported as a warning beside a success, never as an error. Telling somebody
 * the assignment failed when it landed is the lie they act on, by assigning
 * again.
 *
 * ⚠️ `onDone` FIRES EXACTLY ONCE, IMMEDIATELY BEFORE `onClose`, whenever a
 * write landed — on the success that closes the dialog straight away, or on
 * dismissing the email warning. The caller reloads in `onDone`; it may close
 * there too, because `onClose` follows anyway.
 *
 * FOLLOW-UP TASK (owner, 2026-10-16): an assign also creates, moves or cancels
 * a real follow-up task server-side. "Follow up by" is a zone-less wall clock
 * ('YYYY-MM-DD HH:MM') read on the TASK ASSIGNEE's clock, built with the same
 * native date/time inputs and `toDueAtString` as `TaskFormModal` — no `Date`
 * is ever attached to what is sent. Default: the next working day (Mon–Fri) at
 * 10:00. "No follow-up task" sends `createTask: false` and no `followUpAt`.
 * Both fields blank sends neither key (the server's default). An assignee with
 * no EMERGE account gets no extra copy here: the server gives the task to the
 * assigner, and that is said nowhere in the UI (no helper text).
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { Button, Checkbox, Combobox, Field, Input, Modal, Skeleton, type ComboboxOption } from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { api, ApiError } from '@/lib/api';
import { todayIsoDate } from '@/lib/invoice-utils';
import { addDaysIso } from '@/lib/quote-utils';
import { toDueAtString } from '@/lib/task-time';
import type { EnquiryAssignee, EnquiryAssignment, EnquirySource } from '@/lib/types';

export interface AssignEnquiryModalProps {
  open: boolean;
  source: EnquirySource;
  enquiryId: number;
  /** The institution, company or contact the row is about — named in the title. */
  enquiryName: string;
  current: EnquiryAssignment | null;
  onClose: () => void;
  /** A write landed; `null` means the enquiry is now unassigned. */
  onDone: (next: EnquiryAssignment | null) => void;
}

export function AssignEnquiryModal(props: AssignEnquiryModalProps) {
  if (!props.open) return null;
  // Remounted per enquiry, so nothing from one row survives into the next.
  return <AssignEnquiryDialog key={`${props.source}:${props.enquiryId}`} {...props} />;
}

type ListState =
  | { status: 'loading' }
  | { status: 'failed'; message: string }
  | { status: 'ready'; assignees: readonly EnquiryAssignee[] };

/** A landed write waiting to be handed to `onDone` once the reader closes. */
type Landed = { next: EnquiryAssignment | null; emailWarning: string | null };

function assigneeLabel(assignee: { name: string; team: string | null }): string {
  const team = assignee.team?.trim() ?? '';
  return team === '' ? assignee.name : `${assignee.name} — ${team}`;
}

/**
 * The next Monday–Friday after today, as 'YYYY-MM-DD'. Calendar text only: the
 * weekday is read off a UTC midnight built from the date's own parts, so no
 * zone decides which day it is.
 */
function nextWorkingDay(): string {
  let day = addDaysIso(todayIsoDate(), 1);
  for (let guard = 0; guard < 7; guard += 1) {
    const weekday = new Date(
      Date.UTC(Number(day.slice(0, 4)), Number(day.slice(5, 7)) - 1, Number(day.slice(8, 10))),
    ).getUTCDay();
    if (weekday !== 0 && weekday !== 6) return day;
    day = addDaysIso(day, 1);
  }
  return day;
}

const DEFAULT_FOLLOW_UP_TIME = '10:00';

function refusalSentence(error: unknown, verb: 'assign' | 'unassign'): string {
  if (error instanceof ApiError) {
    const server = error.formMessage ?? error.message;
    if (error.status === 403) {
      return `Your account is not allowed to ${verb} this enquiry. ${server}`;
    }
    return server;
  }
  return verb === 'assign'
    ? 'The enquiry could not be assigned. Check your connection and try again.'
    : 'The enquiry could not be unassigned. Check your connection and try again.';
}

function AssignEnquiryDialog({
  source,
  enquiryId,
  enquiryName,
  current,
  onClose,
  onDone,
}: AssignEnquiryModalProps) {
  const [list, setList] = useState<ListState>({ status: 'loading' });
  const [selected, setSelected] = useState<string>('');
  const [pending, setPending] = useState<'assign' | 'unassign' | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [landed, setLanded] = useState<Landed | null>(null);
  const [followUpDate, setFollowUpDate] = useState<string>(nextWorkingDay);
  const [followUpTime, setFollowUpTime] = useState<string>(DEFAULT_FOLLOW_UP_TIME);
  const [noTask, setNoTask] = useState(false);
  const [followUpError, setFollowUpError] = useState<string | null>(null);
  // A ref as well as state: two clicks in one tick both read state as idle.
  const busy = useRef(false);

  const currentActiveId =
    current !== null && current.assignee.active && current.assignee.id !== null
      ? current.assignee.id
      : null;

  useEffect(() => {
    const controller = new AbortController();
    api
      .listEnquiryAssignees({ isActive: true }, controller.signal)
      .then((answer) => {
        if (controller.signal.aborted) return;
        setList({ status: 'ready', assignees: answer.data });
        // Preselect the current assignee only when they are still offered.
        if (currentActiveId !== null && answer.data.some((a) => a.id === currentActiveId)) {
          setSelected(String(currentActiveId));
        }
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        setList({
          status: 'failed',
          message:
            caught instanceof ApiError
              ? (caught.formMessage ?? caught.message)
              : 'Check your connection and try again.',
        });
      });
    return () => controller.abort();
  }, [currentActiveId]);

  const finish = useCallback(() => {
    if (landed !== null) onDone(landed.next);
    onClose();
  }, [landed, onDone, onClose]);

  const close = useCallback(() => {
    // An in-flight write cannot be recalled, so the dialog stays until it settles.
    if (busy.current) return;
    finish();
  }, [finish]);

  const assign = useCallback(async () => {
    if (busy.current || selected === '') return;
    let followUp: { followUpAt?: string; createTask?: boolean } = {};
    if (noTask) {
      followUp = { createTask: false };
    } else if (followUpDate.trim() !== '' || followUpTime.trim() !== '') {
      const wall = toDueAtString(followUpDate, followUpTime);
      if (wall === null) {
        setFollowUpError(
          followUpDate.trim() === '' || followUpTime.trim() === ''
            ? 'Enter both a date and a time.'
            : 'Enter a real date and time.',
        );
        return;
      }
      followUp = { followUpAt: wall };
    }
    busy.current = true;
    setPending('assign');
    setFormError(null);
    setFieldError(null);
    setFollowUpError(null);
    try {
      const result = await api.assignEnquiry(
        source,
        enquiryId,
        Number(selected),
        undefined,
        followUp,
      );
      if (result.email !== null && result.email.status === 'skipped') {
        setLanded({ next: result.data, emailWarning: result.email.reason });
      } else {
        onDone(result.data);
        onClose();
      }
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 422 && caught.fieldErrors.assigneeId) {
        setFieldError(caught.fieldErrors.assigneeId);
      } else if (
        caught instanceof ApiError &&
        caught.status === 422 &&
        caught.fieldErrors.followUpAt
      ) {
        setFollowUpError(caught.fieldErrors.followUpAt);
      } else {
        setFormError(refusalSentence(caught, 'assign'));
      }
    } finally {
      busy.current = false;
      setPending(null);
    }
  }, [selected, noTask, followUpDate, followUpTime, source, enquiryId, onDone, onClose]);

  const unassign = useCallback(async () => {
    if (busy.current) return;
    busy.current = true;
    setPending('unassign');
    setFormError(null);
    setFieldError(null);
    try {
      const result = await api.assignEnquiry(source, enquiryId, null);
      onDone(result.data);
      onClose();
    } catch (caught) {
      setFormError(refusalSentence(caught, 'unassign'));
    } finally {
      busy.current = false;
      setPending(null);
    }
  }, [source, enquiryId, onDone, onClose]);

  const title = `Assign enquiry — ${enquiryName.trim() === '' ? 'this enquiry' : enquiryName}`;

  if (landed !== null) {
    return (
      <Modal
        open
        onClose={finish}
        title={title}
        footer={
          <Button variant="primary" onClick={finish}>
            Close
          </Button>
        }
      >
        <div className="space-y-2 text-sm">
          <p className="text-fg">
            {landed.next === null
              ? 'The enquiry is now unassigned.'
              : `Assigned to ${landed.next.assignee.name}.`}
          </p>
          <Notice tone="warning" role="alert" className="[overflow-wrap:anywhere]">
            The notification email was not sent. {landed.emailWarning}
          </Notice>
        </div>
      </Modal>
    );
  }

  const options: ComboboxOption[] =
    list.status === 'ready'
      ? list.assignees.map((assignee) => ({
          value: String(assignee.id),
          label: assigneeLabel(assignee),
          hint: assignee.email,
        }))
      : [];

  const unchanged = selected !== '' && Number(selected) === currentActiveId;
  const listEmpty = list.status === 'ready' && list.assignees.length === 0;

  return (
    <Modal
      open
      onClose={close}
      title={title}
      footer={
        <>
          <Button variant="secondary" onClick={close} disabled={pending !== null}>
            Cancel
          </Button>
          {current !== null ? (
            <Button
              variant="secondary"
              onClick={() => void unassign()}
              loading={pending === 'unassign'}
              disabled={pending === 'assign'}
            >
              Unassign
            </Button>
          ) : null}
          <Button
            variant="primary"
            onClick={() => void assign()}
            loading={pending === 'assign'}
            disabled={selected === '' || unchanged || pending === 'unassign'}
          >
            Assign
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {current !== null ? (
          <p className="text-sm text-fg-secondary [overflow-wrap:anywhere]">
            Currently assigned to{' '}
            <span className="font-medium text-fg">{assigneeLabel(current.assignee)}</span>
            {current.assignee.active ? null : <span className="text-fg-muted"> (removed)</span>}
          </p>
        ) : null}

        {list.status === 'loading' ? (
          <div className="space-y-1.5" aria-busy="true">
            <Skeleton className="h-3 w-16" />
            <Skeleton className="h-9 w-full" />
          </div>
        ) : list.status === 'failed' ? (
          <p role="alert" className="text-sm text-danger [overflow-wrap:anywhere]">
            The list of people could not be loaded. {list.message}
          </p>
        ) : listEmpty ? (
          <p className="text-sm text-fg-muted">
            No one is set up to receive enquiries yet. People are added in Settings → Notifications.
          </p>
        ) : (
          <Field label="Assign to" error={fieldError ?? undefined}>
            {({ id }) => (
              <Combobox
                id={id}
                value={selected}
                onChange={(value) => {
                  setSelected(value);
                  setFieldError(null);
                }}
                options={options}
                placeholder="Choose a person…"
                searchPlaceholder="Search people"
                invalid={fieldError !== null}
                disabled={pending !== null}
              />
            )}
          </Field>
        )}

        {list.status === 'ready' && !listEmpty ? (
          <div className="space-y-2">
            <Field label="Follow up by" error={followUpError ?? undefined}>
              {({ id, 'aria-describedby': describedBy }) => (
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    id={id}
                    // Native date and time inputs emit YYYY-MM-DD and HH:MM from
                    // local parts, as on `TaskFormModal`. No Date is constructed
                    // on this path, so no zone is attached to what gets sent.
                    type="date"
                    value={followUpDate}
                    invalid={followUpError !== null}
                    aria-describedby={describedBy}
                    aria-label="Follow-up date"
                    disabled={noTask || pending !== null}
                    onChange={(event) => {
                      setFollowUpDate(event.target.value);
                      setFollowUpError(null);
                    }}
                  />
                  <Input
                    type="time"
                    value={followUpTime}
                    invalid={followUpError !== null}
                    aria-describedby={describedBy}
                    aria-label="Follow-up time"
                    disabled={noTask || pending !== null}
                    onChange={(event) => {
                      setFollowUpTime(event.target.value);
                      setFollowUpError(null);
                    }}
                  />
                </div>
              )}
            </Field>
            <Checkbox
              label="No follow-up task"
              checked={noTask}
              disabled={pending !== null}
              onChange={(event) => {
                setNoTask(event.target.checked);
                setFollowUpError(null);
              }}
            />
          </div>
        ) : null}

        {formError !== null ? (
          <p role="alert" className="text-xs text-danger [overflow-wrap:anywhere]">
            {formError}
          </p>
        ) : null}
      </div>
    </Modal>
  );
}
